import cn from 'classnames/bind'
import { MouseEvent, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'

import { Portal } from './portal'
import { Props, SurfacePlacement } from './types'
import { useDropdownPosition } from './use-dropdown-position'

import styles from './plain-dropdown-surface-common.module.scss'

const cx = cn.bind(styles)

export const PlainDropDownSurfaceCommon = ({
  children,
  className,
  surfaceContentClassName,
  id,
  type,
  surfaceContent,
  ariaLabelledby,
  ariaDescribedby,
  onOpenState,
  placement = SurfacePlacement.Bottom,
}: Props) => {
  const anchorRef = useRef<HTMLDivElement>(null)
  const surfaceRef = useRef<HTMLDivElement>(null)
  const mousePosition = useRef({ x: 0, y: 0 })

  const [opened, setOpened] = useState(false)
  const [visible, setVisible] = useState(false)

  const popupId = `${id}-${useId()}`

  const { position, updatePosition } = useDropdownPosition({
    opened,
    anchorRef,
    surfaceRef,
    mousePosition,
    placement,
  })

  const close = useCallback(() => {
    setOpened(false)
  }, [])

  const toggle = useCallback((event?: MouseEvent<HTMLElement>) => {
    event?.preventDefault()
    event?.stopPropagation()

    if (event) {
      mousePosition.current = {
        x: event.clientX,
        y: event.clientY,
      }
    }

    setOpened((prev) => !prev)
  }, [])

  useEffect(() => {
    onOpenState?.(opened)
  }, [opened]) // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (!opened) {
      setVisible(false)
      return
    }

    const id = requestAnimationFrame(() => {
      setVisible(true)
    })

    return () => cancelAnimationFrame(id)
  }, [opened])

  useEffect(() => {
    if (!opened) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node

      if (anchorRef.current?.contains(target) || surfaceRef.current?.contains(target)) {
        return
      }

      close()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [opened, close])

  const areaProps = {
    id,
    'aria-haspopup': type,
    'aria-expanded': opened,
    'aria-controls': popupId,
  } as const

  return (
    <>
      <div ref={anchorRef} className={cx(styles.anchor, className)}>
        {children((event?: MouseEvent<HTMLElement>) => toggle(event), opened, areaProps)}
      </div>

      {opened && (
        <Portal>
          <div
            ref={surfaceRef}
            id={popupId}
            role={type}
            aria-labelledby={ariaLabelledby}
            aria-describedby={ariaDescribedby}
            style={position.style}
            className={cx(
              styles.surface,
              { opened: visible, surfaceTop: position.placement === 'top' },
              surfaceContentClassName,
            )}
          >
            {surfaceContent(toggle, opened, updatePosition)}
          </div>
        </Portal>
      )}
    </>
  )
}
